import type { ComponentProps } from 'react';
import type { ContentType } from '../types';
import { PromotionPodClient } from './PromotionPodClient';
import { PromotionPodView } from './PromotionPodView';
import type { PromotionPodVariant } from './types/types';

type PromotionPodProps = Omit<ComponentProps<typeof PromotionPodClient>, 'children'> & {
  data: Partial<ContentType>;
  variant?: PromotionPodVariant;
  grayscale?: boolean;
  ctaCallback?: () => void;
  moreInfoCtaCallback?: () => void;
};

/**
 * The promotion pod — the single component consumers use, on the server or the client.
 *
 * It composes the client boundary ({@link PromotionPodClient}, which owns the notification logic
 * and shell) with the server-rendered presentational UI ({@link PromotionPodView}) passed as its
 * child.
 *
 * @param className - Positioning/styling for the pod's outer container.
 * @param data - Partial promotion content (title, description, status, image, progress, …).
 * @param variant - 'offers' | 'bonuses' | 'free-spins'. Controls the progress bar and balance display.
 * @param grayscale - Force the graphic into grayscale (also applied automatically for expired pods).
 * @param props - Standard div props applied to the pod's outer container.
 * @returns The composed promotion pod.
 */
function PromotionPod({ className, data, variant = 'offers', grayscale, ctaCallback, moreInfoCtaCallback, ...props }: PromotionPodProps) {
  return (
    <PromotionPodClient data={data} variant={variant} className={className} {...props}>
      <PromotionPodView data={data} variant={variant} grayscale={grayscale} ctaCallback={ctaCallback} moreInfoCtaCallback={moreInfoCtaCallback} />
    </PromotionPodClient>
  );
}

export { PromotionPod };
export type { PromotionPodProps };
